import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  BANK_PAYMENT_DUPLICATE_CODE,
  ORDER_PAYMENT_SOURCE_LABELS_RU,
  ORDER_PAYMENT_STATUS_LABELS_RU,
} from '@crm/shared'
import type { OrderDetailDto, OrderPaymentDto } from '@crm/shared'
import { createPayment, updatePayment, deletePayment } from '../../../shared/api/orders.api'
import { formatMoney, formatDate, round2 } from '../orderFormat'
import { MoneyInput } from './MoneyInput'
import { confirm } from '../../../shared/ui/confirmStore'

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'Не удалось сохранить платёж'
}

/** Сервер просит подтверждения: такую оплату уже принёс банк (защита от задвоения). */
function isBankDuplicate(e: unknown): boolean {
  const err = e as { response?: { data?: { error?: { code?: string } } } }
  return err.response?.data?.error?.code === BANK_PAYMENT_DUPLICATE_CODE
}

type Editing = {
  id: string | null
  amount: number | null
  kind: string
  note: string
  /** Оплата из банка или подтверждённая банком: сумму менять нельзя. */
  locked?: boolean
  confirmDuplicate?: boolean
}

export function PaymentsSection({
  order,
  canMutate,
}: {
  order: OrderDetailDto
  canMutate: boolean
}) {
  const qc = useQueryClient()
  const [edit, setEdit] = useState<Editing | null>(null)
  const [error, setError] = useState<string | null>(null)

  const active = order.status !== 'CANCELLED' && order.status !== 'ARCHIVED'
  // Переплата (хвост №260): разнос из банка больше остатка не кладёт, а ручная
  // оплата могла превысить сумму счетов молча — показываем это явно.
  const overpaid = round2(order.paidSum - order.invoicedSum)
  const editOverpay = (() => {
    if (!edit || edit.locked || order.invoicedSum <= 0) return 0
    const before = edit.id ? (order.payments.find((p) => p.id === edit.id)?.amount ?? 0) : 0
    return round2(order.paidSum - before + (Number(edit.amount) || 0) - order.invoicedSum)
  })()
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['orders', order.id] })

  const save = useMutation({
    mutationFn: (e: Editing) =>
      e.id
        ? updatePayment(e.id, {
            ...(e.locked ? {} : { amount: e.amount ?? undefined }),
            kind: e.kind.trim() || null,
            note: e.note.trim() || null,
          })
        : createPayment(order.id, {
            amount: e.amount ?? 0,
            kind: e.kind.trim() || undefined,
            note: e.note.trim() || undefined,
            ...(e.confirmDuplicate ? { confirmDuplicate: true } : {}),
          }),
    onSuccess: () => {
      setEdit(null)
      invalidate()
    },
    onError: (e, vars) => {
      if (!vars.id && !vars.confirmDuplicate && isBankDuplicate(e)) {
        void confirm({
          title: 'Такая оплата уже пришла из банка',
          message: `${extractError(e)}. Внести ещё одну оплату на ту же сумму?`,
          confirmText: 'Внести вторую оплату',
        }).then((ok) => ok && save.mutate({ ...vars, confirmDuplicate: true }))
        return
      }
      setError(extractError(e))
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => deletePayment(id),
    onSuccess: invalidate,
  })

  const openNew = () => {
    setError(null)
    setEdit({ id: null, amount: null, kind: '', note: '' })
  }
  const openEdit = (p: OrderPaymentDto) => {
    setError(null)
    setEdit({
      id: p.id,
      amount: p.amount,
      kind: p.kind ?? '',
      note: p.note ?? '',
      locked: p.bankConfirmed,
    })
  }

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">
          Оплаты ({order.payments.length})
          {overpaid > 0 && order.invoicedSum > 0 && (
            <span className="ml-2 text-sm font-normal text-amber-700">
              переплата {formatMoney(overpaid)}
            </span>
          )}
        </h2>
        {canMutate && active && (
          <button
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
            onClick={openNew}
          >
            Зафиксировать оплату
          </button>
        )}
      </div>

      {order.payments.length === 0 ? (
        <p className="text-sm text-gray-500">Платежей нет.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="py-1">Дата</th>
              <th className="py-1">Сумма</th>
              <th className="py-1">Назначение</th>
              <th className="py-1">Статус</th>
              <th className="py-1">Источник</th>
              <th className="py-1">Комментарий</th>
              {canMutate && <th className="py-1" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {order.payments.map((p) => (
              <tr key={p.id}>
                <td className="py-1.5 text-gray-600">{formatDate(p.paidAt ?? p.createdAt)}</td>
                <td className="py-1.5 font-medium">{formatMoney(p.amount)}</td>
                <td className="py-1.5 text-gray-600">{p.kind ?? '—'}</td>
                <td className="py-1.5 text-gray-600">{ORDER_PAYMENT_STATUS_LABELS_RU[p.status]}</td>
                <td className="py-1.5 text-gray-600">
                  {ORDER_PAYMENT_SOURCE_LABELS_RU[p.source]}
                  {p.source === 'MANUAL' && p.bankConfirmed ? ' · подтверждена банком' : ''}
                </td>
                <td className="py-1.5 text-gray-500">{p.note ?? ''}</td>
                {canMutate && (
                  <td className="py-1.5 text-right">
                    <button
                      className="rounded-lg px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
                      onClick={() => openEdit(p)}
                    >
                      Править
                    </button>
                    {!p.bankConfirmed && (
                      <button
                        className="rounded-lg px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                        onClick={() => {
                          void confirm({
                            title: 'Удалить платёж?',
                            message: 'Статус оплаты заказа будет пересчитан.',
                            danger: true,
                            confirmText: 'Удалить',
                          }).then((ok) => ok && remove.mutate(p.id))
                        }}
                        disabled={remove.isPending}
                      >
                        Удалить
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
            <h3 className="mb-3 text-lg font-semibold">
              {edit.id ? 'Изменить оплату' : 'Зафиксировать оплату'}
            </h3>
            <label className="mb-3 block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Сумма, ₽</span>
              {edit.locked ? (
                <p className="text-gray-700">
                  {formatMoney(edit.amount ?? 0)}{' '}
                  <span className="text-xs text-gray-500">
                    — оплата из банковской выписки, сумму меняет бухгалтерия в разделе «Банк»
                  </span>
                </p>
              ) : (
                <MoneyInput
                  value={edit.amount}
                  onChange={(v) => setEdit({ ...edit, amount: v })}
                  autoFocus
                />
              )}
            </label>
            <label className="mb-3 block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Назначение</span>
              <input
                className="w-full rounded border border-gray-300 px-2 py-1.5"
                placeholder="предоплата / доплата"
                value={edit.kind}
                onChange={(e) => setEdit({ ...edit, kind: e.target.value })}
              />
            </label>
            <label className="mb-3 block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Комментарий</span>
              <input
                className="w-full rounded border border-gray-300 px-2 py-1.5"
                value={edit.note}
                onChange={(e) => setEdit({ ...edit, note: e.target.value })}
              />
            </label>
            {editOverpay > 0 && (
              <p className="mb-3 text-sm text-amber-700">
                Оплат станет больше суммы счетов на {formatMoney(editOverpay)} — проверьте сумму.
              </p>
            )}
            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <button
                className="rounded-lg px-3 py-1.5 text-sm text-gray-600"
                onClick={() => setEdit(null)}
              >
                Отмена
              </button>
              <button
                className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
                onClick={() => save.mutate(edit)}
                disabled={save.isPending || !(Number(edit.amount) > 0)}
              >
                {save.isPending ? 'Сохраняю…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
